import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { forkJoin, Subscription } from 'rxjs';
import { finalize, switchMap } from 'rxjs/operators';
import { Gruppe } from '../../classes/gruppe';
import { ScanErgebnis } from '../../classes/scan-ergebnis';
import { EinstallenService } from '../einstallen.service';
import { MessageService } from '../../../../services/message-service/message.service';
import {
    EinstallenHitOptionen,
    EinstallenMeldenOverview,
    EinstallenSubmitFlowService,
} from '../einstallen-submit-flow.service';

@Component({
    selector: 'kiene-einstallen-mobile-review',
    templateUrl: './einstallen-mobile-review.component.html',
    styleUrls: ['./einstallen-mobile-review.component.scss'],
})
export class EinstallenMobileReviewComponent implements OnInit, OnDestroy {
    @Input() gruppe: Gruppe;
    @Output() back = new EventEmitter<void>();
    @Output() completed = new EventEmitter<void>();

    isLoadingReview = false;
    isSubmitting = false;
    isClosingTierliste = false;
    canEditGewicht = false;
    overview: EinstallenMeldenOverview = {
        anzahlPassUndOhrmarke: 0,
        anzahlPass: 0,
        anzahlOhrmarke: 0,
        anzahlCsv: 0,
        nichtMeldbar: 0,
    };
    hitOptionen: EinstallenHitOptionen = {
        passUndOhrmarke: false,
        nurPassGescannt: false,
        nurOhrmarkeGescannt: false,
        wederPassNochOhrmarke: false,
    };
    gewichtScans: ScanErgebnis[] = [];

    private reviewSubscription?: Subscription;

    constructor(
        private readonly einstallenService: EinstallenService,
        private readonly einstallenSubmitFlow: EinstallenSubmitFlowService,
        private readonly messageService: MessageService,
    ) {}

    ngOnInit(): void {
        this.canEditGewicht = this.einstallenSubmitFlow.currentUserCanEditGewicht();
        this.loadReview();
    }

    ngOnDestroy(): void {
        this.reviewSubscription?.unsubscribe();
    }

    get last10SuccessfulScansCount(): number {
        return this.einstallenSubmitFlow.getLastSuccessfulScansCount(this.gewichtScans);
    }

    get last10ScansGesamtGewicht(): number {
        return this.einstallenSubmitFlow.getLastSuccessfulScansGesamtGewicht(this.gewichtScans);
    }

    get showLast10ScansGewicht(): boolean {
        return this.last10ScansGesamtGewicht > 0;
    }

    get missingGewichteCount(): number {
        return this.einstallenSubmitFlow.getMissingGewichtScans(this.gewichtScans).length;
    }

    get isBusy(): boolean {
        return this.isLoadingReview || this.isSubmitting || this.isClosingTierliste;
    }

    get canSubmitMelden(): boolean {
        return this.einstallenSubmitFlow.canSubmitMelden(this.hitOptionen, this.overview);
    }

    onBack(): void {
        this.back.emit();
    }

    gewichteBearbeiten(): void {
        if (!this.gruppe?.gruppe_id) {
            return;
        }
        this.einstallenSubmitFlow.openGewichteDialog(this.gruppe.gruppe_id, this.gewichtScans).subscribe(saved => {
            if (saved) {
                this.loadReview();
            }
        });
    }

    abschliessen(): void {
        if (!this.gruppe?.gruppe_id || this.isBusy) {
            return;
        }
        this.isClosingTierliste = true;
        const submitInput = this.einstallenSubmitFlow.buildMeldenSubmitInput(this.hitOptionen, this.gruppe);
        this.einstallenSubmitFlow
            .closeEinstallTierlisteWithoutMelden({
                gruppeId: submitInput.gruppeId,
                gsId: submitInput.gsId,
                einstallTierlisteId: submitInput.einstallTierlisteId,
            })
            .then(closed => {
                if (closed) {
                    this.completed.emit();
                }
            })
            .catch(err => {
                this.messageService.errorMessage('Fehler beim Abschliessen der Liste!', err);
            })
            .finally(() => {
                this.isClosingTierliste = false;
            });
    }

    submit(): void {
        if (!this.gruppe?.gruppe_id || this.isBusy || !this.canSubmitMelden) {
            return;
        }
        const submitInput = this.einstallenSubmitFlow.buildMeldenSubmitInput(this.hitOptionen, this.gruppe);
        if (!submitInput.gruppeId) {
            return;
        }
        this.isSubmitting = true;
        this.einstallenSubmitFlow
            .submitHitMelden(submitInput)
            .then(saved => {
                if (saved) {
                    this.completed.emit();
                }
            })
            .catch(err => {
                this.messageService.errorMessage('Fehler beim Melden der Tiere!', err);
            })
            .finally(() => {
                this.isSubmitting = false;
            });
    }

    private loadReview(): void {
        if (!this.gruppe?.gruppe_id) {
            return;
        }
        this.isLoadingReview = true;
        this.reviewSubscription?.unsubscribe();
        this.reviewSubscription = this.einstallenSubmitFlow
            .ensureFullTierlisteForReview(
                this.gruppe.gruppe_id,
                this.einstallenService.getCurrentEinstallTierlisteId(),
            )
            .pipe(
                switchMap(() =>
                    forkJoin({
                        overview: this.einstallenSubmitFlow.loadMeldenOverview(
                            this.gruppe.gruppe_id,
                            this.einstallenSubmitFlow.getEinstallMeldenOverviewOptions(
                                this.einstallenService.getCurrentEinstallTierlisteId(),
                            ),
                        ),
                        gewichtScans: this.einstallenSubmitFlow.loadGewichtRelevantScans(
                            this.gruppe.gruppe_id,
                            this.einstallenService.getCurrentEinstallTierlisteId(),
                        ),
                    }),
                ),
                finalize(() => (this.isLoadingReview = false)),
            )
            .subscribe({
                next: ({ overview, gewichtScans }) => {
                    this.overview = overview;
                    this.hitOptionen = this.einstallenSubmitFlow.deriveHitOptionenFromOverview(overview);
                    this.gewichtScans = gewichtScans;
                },
                error: err => {
                    this.messageService.errorMessage('Fehler beim Laden!', err);
                },
            });
    }
}
